import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Users, Check, X, Loader2, Mail, Building2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { teamApi } from "../../services/api";
import { useAuthStore } from "../../store/auth.store";

export const InviteAccept = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [success, setSuccess] = useState(false);
  const [declined, setDeclined] = useState(false);

  // ============================================
  // TanStack Query - Fetch Invitation Details
  // ============================================
  const {
    data: invitation,
    isLoading: loading,
    error: fetchError,
  } = useQuery({
    queryKey: ["invitation", token],
    queryFn: async () => {
      const response = await teamApi.getInvitationDetails(token);
      return response.data?.data || response.data;
    },
    enabled: !!token,
    retry: false,
  });

  const error = fetchError
    ? fetchError.response?.data?.error?.message ||
      fetchError.response?.data?.error ||
      "Invalid or expired invitation"
    : null;

  // ============================================
  // TanStack Query - Accept Invitation Mutation
  // ============================================
  const acceptMutation = useMutation({
    mutationFn: () => teamApi.acceptInvitation(token),
    onSuccess: () => {
      setSuccess(true);
      toast.success(t("team.invitationAccepted") || "You've joined the team!");

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    },
    onError: (err) => {
      console.error("Failed to accept invitation:", err);
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to accept invitation";
      toast.error(message);
    },
  });

  // ============================================
  // TanStack Query - Decline Invitation Mutation
  // ============================================
  const declineMutation = useMutation({
    mutationFn: () => teamApi.declineInvitation(token),
    onSuccess: () => {
      setDeclined(true);
      toast.success(t("team.invitationDeclined") || "Invitation declined");
    },
    onError: (err) => {
      console.error("Failed to decline invitation:", err);
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to decline invitation";
      toast.error(message);
    },
  });

  const accepting = acceptMutation.isPending;
  const declining = declineMutation.isPending;

  const handleAccept = () => {
    // If not authenticated, redirect to register with invite token and email
    if (!isAuthenticated) {
      const params = new URLSearchParams({
        invite: token,
        email: invitation.email,
      });
      navigate(`/register?${params.toString()}`);
      return;
    }

    acceptMutation.mutate();
  };

  const handleDecline = () => {
    declineMutation.mutate();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Invalid Invitation
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to the Team!
          </h1>
          <p className="text-gray-600 mb-6">
            You've successfully joined{" "}
            <strong>{invitation?.organisationName}</strong>. Redirecting to
            dashboard...
          </p>
          <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Declined state
  if (declined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-gray-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Invitation Declined
          </h1>
          <p className="text-gray-600 mb-6">
            You've declined the invitation to join{" "}
            {invitation?.organisationName}.
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  // Main invitation view
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            You're Invited!
          </h1>
          <p className="text-gray-600">
            You've been invited to join a team on eScribe
          </p>
        </div>

        {/* Invitation details */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6 space-y-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Organization</p>
              <p className="font-semibold text-gray-900">
                {invitation?.organisationName || "Team"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Invited by</p>
              <p className="font-semibold text-gray-900">
                {invitation?.inviterName ||
                  invitation?.inviterEmail ||
                  "Team Admin"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Your Role</p>
              <p className="font-semibold text-gray-900 capitalize">
                {invitation?.role || "Member"}
              </p>
            </div>
          </div>
        </div>

        {/* Message if present */}
        {invitation?.message && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800 italic">
              "{invitation.message}"
            </p>
          </div>
        )}

        {/* Auth notice */}
        {!isAuthenticated && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              You'll need to create an account or log in to accept this
              invitation.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleAccept}
            disabled={accepting || declining}
            className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {accepting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isAuthenticated ? "Joining..." : "Continue..."}
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                {isAuthenticated ? "Accept & Join Team" : "Accept Invitation"}
              </>
            )}
          </button>

          <button
            onClick={handleDecline}
            disabled={accepting || declining}
            className="w-full px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {declining ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Declining...
              </>
            ) : (
              <>
                <X className="w-5 h-5" />
                Decline Invitation
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{" "}
          <Link
            to={`/login?returnUrl=/invite/${token}`}
            className="text-primary hover:underline"
          >
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default InviteAccept;
